import { merge } from 'lodash';
import type { Integration, IntegrationConfig } from '../types/integration';
import type { IntegrationsStore } from '../types/integrationsStore';

import { Log } from '@devanjs/log';
import type { PossiblyAsyncIntegration } from '../types/asyncIntegration';

export type PatchOptions = {
  setupAt?: Date;
};

export const patch = <
  I extends Integration,
  C extends IntegrationConfig<I>,
  Integrations extends Record<string, PossiblyAsyncIntegration>,
>(
  store: IntegrationsStore<Integrations>,
  integration: I,
  config: C,
  options?: PatchOptions,
) => {
  Log.tag('integrations').space().green('Patching').info(integration.code);

  const installation = store.installations[integration.code];

  if (installation === undefined) {
    throw new Error("Can't patch an integration that hasn't been installed");
  }

  installation.config = () => merge(integration.config(), config);

  // @ts-expect-error Re-sync extendedConfig with the new config
  installation.extendedConfig = installation.config();

  // @ts-expect-error Re-sync setup state from the API
  installation.setupAt = options?.setupAt ?? installation.setupAt;

  Object.values(store.installations).forEach((installation) => {
    const selfConfig = installation.extendedConfig;
    const extensionConfig = store.extensions
      .filter((extension) => extension.target === installation.integration.code)
      .reduce((config, extension) => extension.config(config), installation.config());

    Object.assign(selfConfig, extensionConfig);
  });

  Log.tag('integrations').space().green('Patched').info(integration.code);

  return installation;
};
